export type CopyFieldDef = {
  path: string;
  label: string;
  hint?: string;
  multiline?: boolean;
  wide?: boolean;
  image?: boolean;
};

export type CopyArrayBlockDef = {
  title: string;
  hint?: string;
  index: number;
  basePath: string;
  fields: CopyFieldDef[];
};

export type CopySectionDef = {
  id: string;
  title: string;
  description?: string;
  previewHref?: string;
  fields?: CopyFieldDef[];
  arrays?: CopyArrayBlockDef[];
};

export type CopyTabDef = {
  id: string;
  label: string;
  sections: CopySectionDef[];
};

const FEATURE_ROW_LABELS = ["The Night", "The Voice", "Tune In", "Past Nights"] as const;
const PARTNER_TIER_LABELS = ["Monthly partner", "One-night sponsor", "Recording fund"] as const;
const GALLERY_TILE_LABELS = [
  "Tile 1 — wide room shot",
  "Tile 2 — hands raised",
  "Tile 3 — candle closeup",
  "Tile 4 — Dorothy on stage",
  "Tile 5 — band wide",
  "Tile 6 — crowd from balcony",
] as const;

export const ADMIN_COPY_TABS: CopyTabDef[] = [
  {
    id: "home",
    label: "Home",
    sections: [
      {
        id: "hero",
        title: "Hero banner",
        description: "The first screen visitors see — title, date area, venue, and call-to-action buttons.",
        previewHref: "/",
        fields: [
          { path: "hero.eyebrow", label: "Eyebrow line", hint: "Small line above the main title" },
          { path: "hero.titleTogether", label: "Title line 1 — opening word", hint: 'Usually "Together "' },
          { path: "hero.titleIn", label: "Title line 1 — italic word", hint: 'Shown in italics, e.g. "In"' },
          { path: "hero.titleHeaven", label: "Title line 2 — main word", hint: 'Large display text, e.g. "Heaven"' },
          { path: "hero.titleFlame", label: "Title line 3 — subtitle", hint: 'e.g. "Worship Overnight"' },
          { path: "hero.metaTime", label: "Time range", hint: "Shown under the date when an event is scheduled" },
          { path: "hero.venueK", label: "Venue label" },
          { path: "hero.venueLine1", label: "Venue — line 1" },
          { path: "hero.venueLine2", label: "Venue — line 2" },
          { path: "hero.reserveSeat", label: "Reserve button" },
          { path: "hero.reserveClose", label: "Close RSVP panel" },
          { path: "hero.streamLive", label: "Stream link" },
          { path: "hero.countdownLabel", label: "Countdown prefix", hint: 'Text before the timer, e.g. "Begins in"' },
          { path: "hero.posterAlt", label: "Poster image description", hint: "Accessibility text for the event poster" },
        ],
      },
      {
        id: "features-header",
        title: "Explore section header",
        description: "Intro above the four doorway cards on the home page.",
        previewHref: "/",
        fields: [
          { path: "home.features.sectionEyebrow", label: "Section eyebrow" },
          { path: "home.features.sectionTitle", label: "Section title" },
        ],
      },
      {
        id: "features-rows",
        title: "Explore cards",
        description: "Four cards linking to Event, Dorothy, Live, and Gallery. The first card title is filled in automatically from the featured event date.",
        previewHref: "/",
        arrays: FEATURE_ROW_LABELS.map((title, index) => ({
          title,
          index,
          basePath: "home.features.rows",
          hint: index === 0 ? "Title comes from the scheduled event — only edit eyebrow, body, and button here." : undefined,
          fields: [
            { path: `home.features.rows.${index}.eyebrow`, label: "Card label" },
            ...(index === 0
              ? []
              : [{ path: `home.features.rows.${index}.title`, label: "Card title" } satisfies CopyFieldDef]),
            { path: `home.features.rows.${index}.body`, label: "Description", multiline: true, wide: true },
            { path: `home.features.rows.${index}.cta`, label: "Button text" },
          ],
        })),
      },
      {
        id: "dorothy-teaser",
        title: "Dorothy teaser",
        description: "Quote and story block that links to the full Dorothy page.",
        previewHref: "/",
        fields: [
          { path: "home.dorothyTeaser.eyebrow", label: "Eyebrow" },
          { path: "home.dorothyTeaser.quote", label: "Pull quote", multiline: true, wide: true },
          { path: "home.dorothyTeaser.body", label: "Body paragraph", multiline: true, wide: true },
          { path: "home.dorothyTeaser.cta", label: "Link text" },
          { path: "home.dorothyTeaser.imageAlt", label: "Photo description" },
        ],
      },
      {
        id: "live-cta",
        title: "Live stream callout",
        description: "Mid-page band inviting visitors to join the broadcast.",
        previewHref: "/",
        fields: [
          { path: "home.liveCta.pill", label: "Status pill" },
          { path: "home.liveCta.statWatchLabel", label: "Watching stat label" },
          { path: "home.liveCta.statRegLabel", label: "Registered stat label" },
          { path: "home.liveCta.title", label: "Headline", wide: true },
          { path: "home.liveCta.body", label: "Body", multiline: true, wide: true },
          { path: "home.liveCta.cta", label: "Button text" },
        ],
      },
      {
        id: "partner-cta",
        title: "Partner callout",
        description: "Home-page invitation to the partner page.",
        previewHref: "/",
        fields: [
          { path: "home.partnerCta.eyebrow", label: "Eyebrow" },
          { path: "home.partnerCta.title", label: "Headline" },
          { path: "home.partnerCta.body", label: "Body", multiline: true, wide: true },
          { path: "home.partnerCta.ctaPrimary", label: "Primary button" },
          { path: "home.partnerCta.ctaGhost", label: "Secondary button" },
        ],
      },
    ],
  },
  {
    id: "event",
    label: "Event",
    sections: [
      {
        id: "event-section",
        title: "Event page intro",
        description: "Opening section on the dedicated event page.",
        previewHref: "/event",
        fields: [
          { path: "eventSection.eyebrow", label: "Eyebrow" },
          { path: "eventSection.title", label: "Headline", multiline: true, wide: true, hint: "Line breaks are preserved" },
          { path: "eventSection.lede", label: "Opening paragraph", multiline: true, wide: true },
        ],
      },
    ],
  },
  {
    id: "dorothy",
    label: "Dorothy",
    sections: [
      {
        id: "dorothy-bio",
        title: "Biography",
        description: "Profile section on the Dorothy page.",
        previewHref: "/dorothy",
        fields: [
          {
            path: "dorothy.photoUrl",
            label: "Portrait photo",
            hint: "Upload on the live Dorothy page (pencil on hover) or paste a URL here.",
            image: true,
            wide: true,
          },
          { path: "dorothy.photoCaption", label: "Photo caption" },
          { path: "dorothy.eyebrow", label: "Eyebrow" },
          { path: "dorothy.name", label: "Name" },
          { path: "dorothy.lede", label: "Opening paragraph", multiline: true, wide: true },
          { path: "dorothy.quote", label: "Featured quote", multiline: true, wide: true },
          { path: "dorothy.quoteCite", label: "Quote attribution" },
        ],
      },
      {
        id: "movement",
        title: "Movement timeline header",
        description:
          "Title above the ministry timeline. Milestones are edited on the Dorothy page (pencil on hover) or seeded via the database.",
        previewHref: "/dorothy",
        fields: [
          { path: "movement.eyebrow", label: "Eyebrow" },
          { path: "movement.titleLine1", label: "Title — line 1" },
          { path: "movement.titleLine2", label: "Title — line 2" },
        ],
      },
    ],
  },
  {
    id: "partner",
    label: "Partner",
    sections: [
      {
        id: "partner-intro",
        title: "Partner page intro",
        description: "Header and story above the giving tiers.",
        previewHref: "/partner",
        fields: [
          { path: "partner.eyebrow", label: "Eyebrow" },
          { path: "partner.title", label: "Headline" },
          { path: "partner.lede", label: "Opening paragraph", multiline: true, wide: true },
        ],
      },
      {
        id: "partner-tiers",
        title: "Giving tiers",
        description: "The three partnership options and their button labels. Amounts pre-fill the partner form.",
        previewHref: "/partner",
        arrays: PARTNER_TIER_LABELS.map((title, index) => ({
          title,
          index,
          basePath: "partner.tiers",
          fields: [
            { path: `partner.tiers.${index}.k`, label: "Tier name" },
            { path: `partner.tiers.${index}.v`, label: "Amount shown" },
            { path: `partner.tiers.${index}.note`, label: "Short description", multiline: true, wide: true },
            { path: `partner.tiers.${index}.cta`, label: "Button text" },
            { path: `partner.tiers.${index}.tag`, label: "Highlight badge", hint: "Optional — leave blank to hide" },
          ],
        })),
      },
    ],
  },
  {
    id: "stream",
    label: "Stream",
    sections: [
      {
        id: "stream-page",
        title: "Stream page",
        description: "Copy around the live player and share actions.",
        previewHref: "/live",
        fields: [
          { path: "stream.eyebrow", label: "Eyebrow" },
          { path: "stream.title", label: "Headline" },
          { path: "stream.lede", label: "Opening paragraph", multiline: true, wide: true },
          { path: "stream.liveBadge", label: "Live badge prefix", hint: 'Shown before the countdown, e.g. "LIVE IN —"' },
          { path: "stream.playerTitle", label: "Player title" },
          { path: "stream.playerSub", label: "Player subtitle" },
          { path: "stream.metaWatch", label: "Watching label" },
          { path: "stream.metaReg", label: "Registered label" },
          { path: "stream.metaCountries", label: "Countries label" },
          { path: "stream.share", label: "Share button" },
          { path: "stream.calendar", label: "Calendar button" },
        ],
      },
    ],
  },
  {
    id: "contact",
    label: "Contact",
    sections: [
      {
        id: "contact-page",
        title: "Contact page",
        description: "WhatsApp card, contact form heading, and newsletter block.",
        previewHref: "/contact",
        fields: [
          { path: "contact.waEyebrow", label: "WhatsApp — eyebrow" },
          { path: "contact.waTitle", label: "WhatsApp — headline" },
          { path: "contact.waLede", label: "WhatsApp — intro", multiline: true, wide: true },
          { path: "contact.waCardK", label: "WhatsApp card — label" },
          { path: "contact.waCardV", label: "WhatsApp card — number" },
          { path: "contact.formHeading", label: "Email form heading" },
          { path: "contact.newsEyebrow", label: "Newsletter — eyebrow" },
          { path: "contact.newsTitle", label: "Newsletter — headline" },
          { path: "contact.newsLede", label: "Newsletter — intro", multiline: true, wide: true },
        ],
      },
    ],
  },
  {
    id: "gallery",
    label: "Gallery",
    sections: [
      {
        id: "gallery-header",
        title: "Gallery page header",
        previewHref: "/gallery",
        fields: [
          { path: "gallery.eyebrow", label: "Eyebrow" },
          { path: "gallery.title", label: "Headline" },
        ],
      },
      {
        id: "gallery-tiles",
        title: "Photo tiles",
        description: "Overlay label and caption for each placeholder tile.",
        previewHref: "/gallery",
        arrays: GALLERY_TILE_LABELS.map((title, index) => ({
          title,
          index,
          basePath: "gallery.tiles",
          fields: [
            { path: `gallery.tiles.${index}.label`, label: "Overlay label" },
            { path: `gallery.tiles.${index}.caption`, label: "Caption" },
          ],
        })),
      },
    ],
  },
  {
    id: "blog",
    label: "Blog",
    sections: [
      {
        id: "blog-index",
        title: "Blog index",
        description: "Header and empty-state message on the journal page.",
        previewHref: "/blog",
        fields: [
          { path: "blog.eyebrow", label: "Eyebrow" },
          { path: "blog.title", label: "Headline" },
          { path: "blog.lede", label: "Intro paragraph", multiline: true, wide: true },
          { path: "blog.empty", label: "Empty state message" },
        ],
      },
    ],
  },
  {
    id: "live",
    label: "Live room",
    sections: [
      {
        id: "live-offline",
        title: "When not broadcasting",
        description: "Messages shown on the live page between overnights.",
        previewHref: "/live",
        fields: [
          { path: "live.offlineNote", label: "Offline notice", multiline: true, wide: true },
          { path: "live.playlistTitle", label: "Replay list heading" },
          { path: "live.commentPendingNote", label: "Comment submitted notice", multiline: true, wide: true },
        ],
      },
    ],
  },
];

export const ADMIN_COPY_TAB_IDS = ADMIN_COPY_TABS.map((t) => t.id);
